import { createContext, useContext, useState, ReactNode, useEffect, Dispatch, SetStateAction, useMemo } from 'react';
import Cookies from 'js-cookie';
import { config } from '../config';
import { AuthUser, UserRoles } from 'store/models/User';
import { decodeJwtManually } from 'utils/decodeJwtManually';

interface AuthContextType {
  isAuthenticated: boolean;
  isPrivacyAccepted: boolean;
  loading: boolean;
  userEmail: string;
  currentUser: AuthUser | null;
  currentUserRoles: UserRoles[] | null;
  logout: () => void;
  setUserEmail: (email: string) => void;
  togglePrivacyStatement: () => void;
  setIsPrivacyAccepted: (isAccepted: boolean) => void;
  setCurrentUserRoles: Dispatch<SetStateAction<UserRoles[] | null>>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Custom Hook for consuming AuthContext
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export const getCurrentUserJWTToken = () => {
  return Cookies.get(config.jwtTokenCookieName);
};

export const getRefreshToken = () => {
  return Cookies.get('refreshToken');
};

type AuthProviderProps = Readonly<{
  children: ReactNode;
}>;

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [currentUserRoles, setCurrentUserRoles] = useState<UserRoles[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true); // New loading state
  const [userEmail, setUserEmail] = useState<string>('');
  const [isPrivacyAccepted, setIsPrivacyAccepted] = useState<boolean>(true);

  const togglePrivacyStatement = () => {
    setIsPrivacyAccepted((isPrivacyAccepted) => !isPrivacyAccepted);
  };

  // Check if JWT exists in cookie on app load
  useEffect(() => {
    const token = getCurrentUserJWTToken();

    if (token) {
      const userData = decodeJwtManually(token);
      setCurrentUser(userData);
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  function logout() {
    Cookies.remove(config.jwtTokenCookieName, { domain: config.cookieDomain });
    Cookies.remove('idToken', { domain: config.cookieDomain });
    Cookies.remove('refreshToken', { domain: config.cookieDomain });
    setIsAuthenticated(false);
    setUserEmail('');
    setCurrentUser(null);
    setIsPrivacyAccepted(true);
  }

  const value = useMemo(
    () => ({
      isAuthenticated,
      loading,
      userEmail,
      currentUser,
      isPrivacyAccepted,
      currentUserRoles,
      logout,
      setUserEmail,
      togglePrivacyStatement,
      setIsPrivacyAccepted,
      setCurrentUserRoles,
    }),
    [
      isAuthenticated,
      loading,
      userEmail,
      currentUser,
      isPrivacyAccepted,
      currentUserRoles,
      logout,
      setUserEmail,
      togglePrivacyStatement,
      setIsPrivacyAccepted,
      setCurrentUserRoles,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
